/**
 * The element that plays a recording (UC-8, light): an `<audio>` of the hook's own, never in the
 * document, fed by the object URL of the fetched audio and driven from the scrubber and from the
 * transcript — a tap on a turn plays from its start. One element per source: it is made when the
 * source is here and lets its media go when the source changes or the page goes. `failed` says
 * the browser could not play what it was given: the page then says so and the transcript stays
 * readable. A page opened at a time (`?t=`) puts every element there once it has read its
 * metadata — the playback copy that replaces the recording as received starts there too — and
 * never plays on its own: a browser plays only from a tap.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PlaybackState } from '@/components/humint/questioning/questioning-types'
import {
  type AudioReadout,
  readoutAt,
  seekSecondsFor,
  startSecondsFor,
  toPlaybackState,
} from './playback-state'

export interface AudioPlayback {
  playback: PlaybackState
  /** The browser cannot play this audio. */
  failed: boolean
  /** Seek and play. Call it inside the tap. */
  playFrom: (ms: number) => void
  /** Seek without changing whether it plays. */
  seek: (ms: number) => void
  toggle: () => void
}

/** Everything after which the element's position, length or playing state may have changed. */
const READOUT_EVENTS = [
  'loadedmetadata',
  'durationchange',
  'timeupdate',
  'seeked',
  'play',
  'pause',
  'ended',
] as const

const readoutOf = (element: HTMLAudioElement): AudioReadout => ({
  currentTime: element.currentTime,
  duration: element.duration,
  paused: element.paused,
  ended: element.ended,
})

/** Stop it and let it drop its media, and with it the blob behind the object URL. */
function release(element: HTMLAudioElement): void {
  element.pause()
  element.removeAttribute('src')
  element.load()
}

/** Put an element that just read its metadata at the time asked for; one past its length is ignored. */
function seekToStart(audio: HTMLAudioElement, startMs: number | null | undefined): void {
  const seconds = startSecondsFor(startMs, audio.duration)
  if (seconds !== null) audio.currentTime = seconds
}

/**
 * The element for one source at a time, what is read of it, and whether it gave up on it.
 * `startMs`: where every element starts, and where the one playing goes when it changes.
 */
function useAudioElement(src: string | null, startMs: number | null | undefined) {
  const element = useRef<HTMLAudioElement | null>(null)
  const [readout, setReadout] = useState<AudioReadout>(() => readoutAt(startMs))
  const [failed, setFailed] = useState(false)
  // Read when an element gets its metadata, whenever that is.
  const start = useRef(startMs)

  // Before the element's own effect, so a new element finds the time already here. Another
  // link to the same recording changes only the time: the element it has goes there.
  useEffect(() => {
    start.current = startMs
    const audio = element.current
    if (!audio) return
    if (audio.readyState < HTMLMediaElement.HAVE_METADATA) {
      // Without its metadata the element is put there once it has it; the readout says so now.
      setReadout(readoutAt(startMs))
      return
    }
    // The beginning is a time like any other here: `?t=0` takes the player back to it.
    const seconds = seekSecondsFor(startMs, audio.duration)
    if (seconds !== null) audio.currentTime = seconds
    setReadout(readoutOf(audio))
  }, [startMs])

  useEffect(() => {
    // Another source starts where the page was opened at, with nothing said of the last one.
    setReadout(readoutAt(start.current))
    setFailed(false)
    if (!src) return
    const audio = new Audio()
    audio.preload = 'metadata'
    const read = () => setReadout(readoutOf(audio))
    const fail = () => setFailed(true)
    // First of the listeners: the readout of the metadata already shows where it was put.
    const toStart = () => seekToStart(audio, start.current)
    audio.addEventListener('loadedmetadata', toStart, { once: true })
    for (const event of READOUT_EVENTS) audio.addEventListener(event, read)
    audio.addEventListener('error', fail)
    audio.src = src
    element.current = audio
    return () => {
      element.current = null
      audio.removeEventListener('loadedmetadata', toStart)
      for (const event of READOUT_EVENTS) audio.removeEventListener(event, read)
      audio.removeEventListener('error', fail)
      release(audio)
    }
  }, [src])

  return { element, readout, setReadout, failed, setFailed }
}

/**
 * `src`: the object URL to play, or null while there is none. `fallbackDurationMs`: the
 * recording's length until the element knows its own. `startMs`: the time the page was opened
 * at, or none.
 */
export function useAudioPlayback(
  src: string | null,
  fallbackDurationMs: number,
  startMs?: number | null
): AudioPlayback {
  const { element, readout, setReadout, failed, setFailed } = useAudioElement(src, startMs)

  const play = useCallback(() => {
    const audio = element.current
    // No decoder for it: say so. A play cut short by a pause or a seek, or one the browser
    // wants a tap for, is nothing to tell.
    audio?.play().catch((error: unknown) => {
      const unsupported = (error as { name?: unknown } | null)?.name === 'NotSupportedError'
      if (unsupported && element.current === audio) setFailed(true)
    })
  }, [element, setFailed])

  const seek = useCallback(
    (ms: number) => {
      const audio = element.current
      if (!audio) return
      audio.currentTime = ms / 1000
      // The scrubber follows at once; the element's own `seeked` confirms it.
      setReadout(current => ({ ...current, currentTime: ms / 1000 }))
    },
    [element, setReadout]
  )

  const playFrom = useCallback(
    (ms: number) => {
      seek(ms)
      play()
    },
    [seek, play]
  )

  const toggle = useCallback(() => {
    const audio = element.current
    if (!audio) return
    if (audio.paused) play()
    else audio.pause()
  }, [element, play])

  const playback = useMemo(
    () => toPlaybackState(readout, fallbackDurationMs),
    [readout, fallbackDurationMs]
  )
  return { playback, failed, playFrom, seek, toggle }
}
